import Checkbox from '@/components/UI/FormElements/Checkbox/Checkbox';
import Icon from '@/components/UI/Icon';
import { _isNotBlank } from '@/littledash';
import type { UserApiId, UserSummaryV1 } from '@/model/User.model';
import { type FC, useMemo, useState } from 'react';
import styles from './UserGroupMemberPicker.module.scss';

interface UserGroupMemberPickerProps {
  users: Array<UserSummaryV1>;
  loading: boolean;
  failed: boolean;
  selected: Array<UserApiId>;
  onChange: (selected: Array<UserApiId>) => void;
  onRetry: () => void;
  disabled?: boolean;
}

/**
 * Two-panel member picker shared by the create and edit group modals: every eligible user on the
 * left with a checkbox, the chosen ones listed on the right.
 *
 * The whole user list is loaded by the caller and filtered here rather than queried per keystroke,
 * so "Select all" has an unambiguous meaning and ticks survive searching.
 */
const UserGroupMemberPicker: FC<UserGroupMemberPickerProps> = ({
  users,
  loading,
  failed,
  selected,
  onChange,
  onRetry,
  disabled = false,
}) => {
  const [search, setSearch] = useState('');

  const selectedApiIds = useMemo(() => new Set(selected), [selected]);

  const visibleUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!_isNotBlank(query)) {
      return users;
    }
    return users.reduce<Array<UserSummaryV1>>((acc, user) => {
      if (user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query)) {
        acc.push(user);
      }
      return acc;
    }, []);
  }, [users, search]);

  const selectedUsers = useMemo(
    () =>
      users.reduce<Array<UserSummaryV1>>((acc, user) => {
        if (selectedApiIds.has(user.api_id)) {
          acc.push(user);
        }
        return acc;
      }, []),
    [users, selectedApiIds]
  );

  const toggleMember = (apiId: UserApiId): void => {
    onChange(selectedApiIds.has(apiId) ? selected.filter((id) => id !== apiId) : [...selected, apiId]);
  };

  const selectAllVisible = (): void => {
    const additions = visibleUsers.reduce<Array<UserApiId>>((acc, user) => {
      if (!selectedApiIds.has(user.api_id)) {
        acc.push(user.api_id);
      }
      return acc;
    }, []);
    onChange([...selected, ...additions]);
  };

  return (
    <div
      className="flex"
      style={{ gap: '1rem' }}
      data-test-component="UserGroupMemberPicker"
      data-test-element="container"
    >
      <div className={`${styles.panel} w-50`} data-test-component="UserGroupMemberPicker" data-test-element="available">
        <div className="pa2 bb b--moon-gray" data-test-component="UserGroupMemberPicker" data-test-element="search">
          <input
            type="search"
            className="w-100"
            style={{ marginBottom: 0 }}
            placeholder="Search"
            aria-label="Search users"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
            disabled={disabled}
            data-testid="member-search-input"
          />
          <div
            className="flex items-center justify-between pt2"
            data-test-component="UserGroupMemberPicker"
            data-test-element="actions"
          >
            <div>
              <button
                type="button"
                className="link-button f6"
                onClick={selectAllVisible}
                disabled={disabled || loading || failed}
                data-testid="select-all-button"
              >
                Select all
              </button>
              <button
                type="button"
                className="link-button f6 ml3"
                onClick={() => onChange([])}
                disabled={disabled || loading || failed || selected.length === 0}
                data-testid="clear-selection-button"
              >
                Clear selection
              </button>
            </div>
            <span className="f6 dark-gray" data-testid="selected-count">
              {selected.length} selected
            </span>
          </div>
        </div>
        <div className={styles.list} data-test-component="UserGroupMemberPicker" data-test-element="list">
          {loading && (
            <p className="f6 dark-gray pa3 ma0" data-testid="members-loading">
              Loading users…
            </p>
          )}
          {failed && (
            <div className="pa3" data-testid="users-error">
              <p className="f6 red ma0 pb2">The users could not be loaded.</p>
              <button type="button" className="link-button f6" onClick={onRetry} data-testid="retry-users-button">
                Try again
              </button>
            </div>
          )}
          {!loading && !failed && visibleUsers.length === 0 && (
            <p className="f6 dark-gray pa3 ma0" data-testid="no-users-message">
              No users match that search
            </p>
          )}
          {visibleUsers.map((user) => (
            <div key={user.api_id} className={`${styles.row} pa2`} data-test-element="user" data-test-key={user.api_id}>
              <Checkbox
                name={user.api_id}
                id={`member-${user.api_id}`}
                checked={selectedApiIds.has(user.api_id)}
                onChange={() => toggleMember(user.api_id)}
                disabled={disabled}
                label={user.name}
                sublabel={user.email}
                testPrefix={`member-${user.api_id}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div
        className={`${styles.panel} ${styles.selected} w-50`}
        data-test-component="UserGroupMemberPicker"
        data-test-element="selected"
      >
        {selectedUsers.length === 0 && (
          <p className="f6 dark-gray pa3 ma0" data-testid="no-members-message">
            No users selected
          </p>
        )}
        {selectedUsers.map((user) => (
          <div
            key={user.api_id}
            className={`${styles.row} flex items-center justify-between pa3 bg-white`}
            data-test-element="selected-user"
            data-test-key={user.api_id}
          >
            <div>
              <p className="ma0 f6" data-testid="selected-member-name">
                {user.name}
              </p>
              <p className="ma0 f6 dark-gray" data-testid="selected-member-email">
                {user.email}
              </p>
            </div>
            <button
              type="button"
              className="link-button"
              aria-label={`Remove ${user.name}`}
              onClick={() => toggleMember(user.api_id)}
              disabled={disabled}
              data-testid="remove-member-button"
            >
              <Icon icon="close" width="12" height="12" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserGroupMemberPicker;
